/*
 * Centralised API service for Prime Wiki Services.
 * - getGeoLocation(): fetch visitor location. Tries the upstream API directly
 *   first; if the browser origin isn't whitelisted (401) or it errors out,
 *   falls back to the FastAPI backend proxy at /api/geo.
 * - submitLead(payload): POST lead to CRM. Handles 200/400/401 + ASP.NET
 *   ProblemDetails validation errors.
 *
 * All signup / popup forms MUST use these helpers (no per-component fetch calls).
 */
import axios from "axios";

const GEO_URL = "https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation";
const LEAD_URL = "https://leadapi.crmbytes.com/api/Lead/AddLead";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_GEO = `${BACKEND}/api/geo`;
const BACKEND_LEAD = `${BACKEND}/api/lead`;

let geoCache = null;
let geoPromise = null;

function isValidGeo(body) {
  return Boolean(body && body.isSuccess && body.data && body.data.ipAddress);
}

async function fetchGeoDirect() {
  try {
    const res = await axios.get(GEO_URL, {
      headers: { accept: "text/plain" },
      timeout: 10000,
      validateStatus: () => true,
    });
    if (res.status === 200 && isValidGeo(res.data)) return res.data;
  } catch (err) {
    // fall through to proxy
    console.warn("[api] geo direct failed", err?.message || err);
  }
  return null;
}

async function fetchGeoProxy() {
  if (!BACKEND) return null;
  try {
    const res = await axios.get(BACKEND_GEO, { timeout: 12000, validateStatus: () => true });
    if (res.status === 200 && isValidGeo(res.data)) return res.data;
  } catch (err) {
    console.warn("[api] geo proxy failed", err?.message || err);
  }
  return null;
}

export async function getGeoLocation() {
  if (geoCache) return geoCache;
  if (geoPromise) return geoPromise;

  geoPromise = (async () => {
    const direct = await fetchGeoDirect();
    if (direct) {
      geoCache = direct;
      return direct;
    }
    const proxied = await fetchGeoProxy();
    if (proxied) {
      geoCache = proxied;
      return proxied;
    }
    geoPromise = null;
    return null;
  })();

  return geoPromise;
}

/**
 * Guarantees a usable IP for the CRM payload.
 *
 * Tries the cached geo result first; if that doesn't yield an IP, forces a
 * fresh call to the backend geo proxy (which injects the real visitor IP from
 * X-Forwarded-For when the upstream lookup fails). The CRM rejects lead
 * submissions where `ipAddress` is empty with "IP address is required", so
 * this fallback is critical.
 */
async function ensureIpAddress() {
  const cached = geoCache && geoCache.data && geoCache.data.ipAddress;
  if (cached) return cached;
  // Force a fresh proxy call — ignore cached null.
  try {
    if (BACKEND) {
      const res = await axios.get(BACKEND_GEO, { timeout: 8000, validateStatus: () => true });
      if (res.status === 200 && res.data && res.data.data && res.data.data.ipAddress) {
        // Seed the cache for downstream calls.
        geoCache = res.data;
        return res.data.data.ipAddress;
      }
    }
  } catch (_) {
    /* ignore */
  }
  return "";
}

function flattenValidationErrors(errors) {
  if (!errors || typeof errors !== "object") return "";
  const parts = [];
  Object.keys(errors).forEach((k) => {
    const v = errors[k];
    if (Array.isArray(v)) parts.push(v.join(", "));
    else if (typeof v === "string") parts.push(v);
  });
  return parts.join(" · ");
}

/**
 * Submit a lead to the CRM.
 * Returns { ok:true, customerId, data } on success
 *         { ok:false, statusCode, message } on failure.
 */
export async function submitLead(lead) {
  const geo = await getGeoLocation();
  const geoData = geo && geo.data ? geo.data : null;

  // CRM hard-requires a non-empty ipAddress — fall back to the backend proxy
  // (which attaches the real visitor IP from X-Forwarded-For) when the
  // upstream geolocation lookup failed silently.
  let ipAddress = geoData?.ipAddress || "";
  if (!ipAddress) ipAddress = await ensureIpAddress();

  const payload = {
    name: (lead.name || "").trim(),
    email: (lead.email || "").trim(),
    countryDialCode: Number(lead.countryDialCode) || 0,
    country: lead.country || (geoData?.countryCode ?? ""),
    // Phone must be digits only, no dial code concatenation
    phoneNumber: String(lead.phoneNumber || "").replace(/\D+/g, ""),
    ipAddress: ipAddress,
    description: lead.description || "",
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    city: geoData?.cityName || "",
    state: geoData?.regionName || "",
    geoLocation: geo ? JSON.stringify(geo) : "",
    packageTitle: lead.packageTitle || "",
    packageAmount: lead.packageAmount ?? 0,
  };

  try {
    // Primary: direct CRM call from the browser — the user's Origin header is
    // what the CRM's allowlist expects.
    let res = await axios.post(LEAD_URL, payload, {
      headers: { "Content-Type": "application/json", accept: "text/plain" },
      timeout: 15000,
      validateStatus: () => true,
    });

    // If network/CORS issues caused a transport-level failure, try the
    // backend proxy as a last resort.
    if (!res || res.status === 0 || res.status >= 500) {
      if (BACKEND_LEAD) {
        res = await axios.post(BACKEND_LEAD, payload, {
          headers: { "Content-Type": "application/json", accept: "text/plain" },
          timeout: 20000,
          validateStatus: () => true,
        });
      }
    }

    const body = res.data || {};
    const status = body.statusCode || res.status;

    if (status === 200 && body?.data?.customerId) {
      return { ok: true, customerId: body.data.customerId, data: body.data };
    }

    if (status === 401) {
      return {
        ok: false,
        statusCode: 401,
        message: body.message || "Unauthorized. Please try again later.",
      };
    }

    if (status === 400) {
      const validationMsg = flattenValidationErrors(body.errors);
      return {
        ok: false,
        statusCode: 400,
        message:
          body.errorMessage ||
          body?.data?.message ||
          validationMsg ||
          body.title ||
          "Invalid submission.",
      };
    }

    return {
      ok: false,
      statusCode: status,
      message:
        body.errorMessage ||
        body.message ||
        flattenValidationErrors(body.errors) ||
        body.title ||
        "Something went wrong. Please try again.",
    };
  } catch (err) {
    return {
      ok: false,
      statusCode: 0,
      message: err?.message || "Network error. Please try again.",
    };
  }
}

// After a successful lead submission, redirect per spec:
// /application/main?customerid={customerId}
export function redirectToApplication(customerId) {
  if (typeof window !== "undefined") {
    window.location.href = `/thank?customerid=${encodeURIComponent(customerId)}`;
  }
}
